import { forwardRef, useState } from 'react';
import RcInput, { InputProps, InputRef } from 'rc-input';
import { useDebounce } from 'react-use';
import { IconButton } from '../button';
import clsx from 'clsx';

export type SearchBarProps = Omit<
  InputProps,
  'classNames' | 'onChange' | 'onPressEnter'
> & {
  debounceTime?: number;
  onChangeHandler?: (value: string) => void;
  onSearch?: (value: string) => void;
  defaultValue?: string;
};

export const SearchBar = forwardRef<InputRef, SearchBarProps>(
  (props, forwardRef) => {
    const {
      placeholder = '特力屋',
      debounceTime = 0,
      onChangeHandler,
      onSearch,
      defaultValue = '',
    } = props;
    const [value, setValue] = useState(defaultValue);
    const [isTypingFinish, setIsTypingFinish] = useState(true);

    function handleClear() {
      setValue('');
    }

    function handleSearch() {
      onSearch?.(value || placeholder);
    }

    useDebounce(
      () => {
        onChangeHandler?.(value);
      },
      debounceTime,
      [value]
    );

    const handleCompositionStart = () => {
      setIsTypingFinish(false);
    };

    const handleCompositionEnd = () => {
      setTimeout(() => {
        setIsTypingFinish(true);
      }, 300);
    };

    const handlePressEnter = () => {
      if (!isTypingFinish) return;
      handleSearch();
    };

    return (
      <RcInput
        ref={forwardRef}
        classNames={{
          affixWrapper:
            'tlw-h-10 tlw-px-3 tlw-py-2 tlw-flex tlw-justify-between tlw-items-center tlw-gap-1 tlw-bg-white tlw-rounded-full tlw-border tlw-border-gray-300',
          input:
            'tlw-txt-zh-body-2 tlw-text-txt-800 tlw-flex-1 tlw-caret-pr placeholder:tlw-text-txt-500',
          suffix: 'tlw-size-6 tlw-flex tlw-justify-center tlw-items-center',
        }}
        value={value}
        onChange={(event) => setValue(event.currentTarget.value)}
        placeholder={placeholder}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onPressEnter={handlePressEnter}
        suffix={
          <IconButton
            className={clsx('tlw-p-0', !value && 'search__button')}
            icon={value ? 'X' : 'Search'}
            iconSize={value ? 20 : 24}
            square
            onClick={value ? handleClear : handleSearch}
          />
        }
        {...props}
      />
    );
  }
);
